import React, { useState } from "react";
import { Row, Container, Col, Pagination, Spinner } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import UserItemShop from "../../components/UserShopItem/UserItemShop";
import UserShopProfile from "../../components/UserShopProfile/UserShopProfile";
import BackArrow from "../../components/backArrow";
import noImagePlaceholder from "../../assets/images/dummy/no_image_placeholder.png";

import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getAsset, formatCurrencyCode } from "../../helpers/helper";
import {
  useGetShopByIdQuery,
  useGetShopItemsQuery,
} from "../../features/shop/shopApiSlice";
import { decodeId } from "../../utils/idObfuscator";

const LIMIT = 15;

const WholesellerItem = () => {
  const { shopid: rawShopid } = useParams();
  const shopid = decodeId(rawShopid);
  const location = useLocation();
  const { name } = useSelector((state) => state.user);

  // Pagination page state
  const [page, setPage] = useState(1);

  const [lgShowreport, setLgShowreport] = useState(false);
  const [createShop, setCreateShop] = useState({
    userid: name?._id,
    username: name?.fullname,
    imgurl: name?.imageUrl,
    problem: "",
    desciription: "",
    image: [""],
  });

  /* ===========================
     RTK Query: Fetch Shop Profile
  ============================ */
  const { data: shopRes } = useGetShopByIdQuery(shopid, { skip: !shopid });
  const shopData = shopRes?.data?.Shop;

  /* ===========================
     RTK Query: Fetch Shop Items (paginated)
  ============================ */
  const { data: itemsRes, isFetching: loading } = useGetShopItemsQuery(
    { shopid, page, limit: LIMIT },
    { skip: !shopid }
  );

  const items = itemsRes?.regular || [];
  const totalPages = itemsRes?.totalPages || 1;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /* ===========================
     Render Item Cards
  ============================ */
  const renderItemCards = () => {
    if (!loading && !items.length) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "300px" }}
        >
          <p className="text-muted">No items found for this shop.</p>
        </div>
      );
    }

    return (
      <div className="row ps-2 pe-2">
        {items.map((item) => {
          const rawImg = item?.images?.[0] || item?.image?.[0];
          const imgSrc = rawImg ? getAsset(rawImg) : noImagePlaceholder;
          return <ItemCard key={item._id} item={item} imgSrc={imgSrc} />;
        })}
      </div>
    );
  };

  return (
    <Container className="user-shop-page">
      <div className="user-shop-mobile-back d-lg-none">
        <BackArrow back="/buy-wholeseller" />
      </div>
      <Row>
        {/* ================= LEFT PROFILE ================= */}
        <Col lg="4" className="screenHeight user-shop-profile-column">
          <div className="border b-radius height_100">
            {shopData ? (
              <UserShopProfile
                shopData={shopData}
                shopid={shopid}
                lgShowreport={lgShowreport}
                setLgShowreport={setLgShowreport}
                setCreateShop={setCreateShop}
              />
            ) : (
              <Skeleton height={400} />
            )}
          </div>
        </Col>

        {/* ================= RIGHT ITEMS ================= */}
        <Col lg="8" className="screenHeight user-shop-items-column">
          <div className="border b-radius">
            <div className="ms-3 d-none d-lg-block">
              <BackArrow back="/buy-wholeseller" />
            </div>

            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "300px" }}
              >
                <Spinner
                  animation="border"
                  role="status"
                  style={{ color: "#0353A4", width: "48px", height: "48px" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <>
                {/* Pass fetched items to UserItemShop for tab/view compatibility */}
                <UserItemShop
                  shopid={shopid}
                  regular={items}
                  newItemdata={items}
                  shopData={shopData}
                  isDynamic={true}
                />

                {/* Server-side Pagination Controls */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-3 mb-3">
                    <Pagination>
                      <Pagination.First
                        onClick={() => handlePageChange(1)}
                        disabled={page === 1}
                      />
                      <Pagination.Prev
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                      />
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Pagination.Item
                          key={i + 1}
                          active={i + 1 === page}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                      />
                      <Pagination.Last
                        onClick={() => handlePageChange(totalPages)}
                        disabled={page === totalPages}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

/* ===========================
   Item Card Sub-Component
   - Circle spinner while image loads
   - Custom "No Image Found" placeholder on error or missing
============================ */
const ItemCard = ({ item, imgSrc }) => {
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  const stock = item?.stock || 0;
  const currency = formatCurrencyCode(item?.currency);
  const price = item?.price || 0;
  const itemName = item?.itemname || item?.subCategory || "Item Name";

  return (
    <div className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3 p-2">
      <div className="ShopsCards mt-2 mb-2">
        {/* Image with Loading Spinner + No Image fallback */}
        <div className="fix-img position-relative">
          {imgLoading && !imgError && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{ backgroundColor: "rgba(255,255,255,0.75)", zIndex: 1 }}
            >
              <Spinner
                animation="border"
                size="sm"
                style={{ color: "#0353A4" }}
              />
            </div>
          )}
          <img
            className="card-img img-fluid"
            src={imgError ? noImagePlaceholder : imgSrc}
            alt={itemName}
            style={{
              objectFit: "cover",
              width: "100%",
              height: "-webkit-fill-available",
            }}
            crossOrigin="anonymous"
            onLoad={() => setImgLoading(false)}
            onError={() => {
              setImgLoading(false);
              setImgError(true);
            }}
          />
        </div>

        <div className="border">
          <div>
            <Row className="align-items-center pt-2">
              <Col>
                <p className="mb-0 ps-2 pe-2 items-card-font fw-bold">
                  {itemName.slice(0, 6) + (itemName.length > 6 ? "..." : "")}
                </p>
              </Col>
              <Col>
                <p className="mb-0 ps-1 pe-1 mt-1 stock me-2">{stock} Stock</p>
              </Col>
            </Row>
            <Row className="ps-2">
              <Col lg={6} className="ps-1 pe-0">
                <p className="mb-0 ps-2 pe-2 pt-1 items-card-font font-blue">
                  {currency}&nbsp;{price}
                </p>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesellerItem;
